import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Container,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useMasterData } from "../../../hooks/useMasterData";
import { SessionRecord } from "../../../services/master-data.service";
import reStudyApi, { ReStudyRecord } from "./reStudyApi";
import AddReStudyDialog from "./AddReStudyDialog";

const ReStudyList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { masters, fetchMasters, fetchSections } = useMasterData();

  // Data State
  const [records, setRecords] = useState<ReStudyRecord[]>([]);
  const [loading, setLoading] = useState(false);

  // Filter State
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    sessionId: "",
    classId: "",
    sectionId: "",
  });

  // Master Data State
  const [sections, setSections] = useState<any[]>([]);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchMasters();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [filters, search]);

  useEffect(() => {
    if (filters.classId) {
      (async () => {
        const secs = await fetchSections(filters.classId);
        setSections(secs);
      })();
    } else {
      setSections([]);
    }
  }, [filters.classId]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const data = await reStudyApi.findAll({
        ...filters,
        search: search || undefined,
      });
      setRecords(data);
    } catch (error) {
      console.error("Failed to fetch re-study records", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to remove this re-study record?")) {
      try {
        await reStudyApi.remove(id);
        fetchRecords();
      } catch (error) {
        console.error("Failed to remove re-study record", error);
      }
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Re-Study Students
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage students who repeat the same class in the next academic session.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5">Records</Typography>
        {hasPermission("RE_STUDY_MANAGE") && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Re-Study Student
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Session"
              select
              value={filters.sessionId}
              onChange={(e) => handleFilterChange("sessionId", e.target.value)}
            >
              <MenuItem value="">All Sessions</MenuItem>
              {masters.academicSessions.map((s: SessionRecord) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Class"
              select
              value={filters.classId}
              onChange={(e) => handleFilterChange("classId", e.target.value)}
            >
              <MenuItem value="">All Classes</MenuItem>
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Section"
              select
              value={filters.sectionId}
              onChange={(e) => handleFilterChange("sectionId", e.target.value)}
              disabled={!filters.classId}
            >
              <MenuItem value="">All Sections</MenuItem>
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Student..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell>Adm No / Scholar No</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Prev Session / Class</TableCell>
              <TableCell>New Session / Class</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              records.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.student.admissionNumber} / {row.student.scholarNumber || "-"}
                  </TableCell>
                  <TableCell>
                    {row.student.firstName} {row.student.lastName}
                  </TableCell>
                  <TableCell>
                    {row.previousSession.name} / {row.previousClass.name}
                  </TableCell>
                  <TableCell>
                    {row.newSession.name} / {row.class.name}
                  </TableCell>
                  <TableCell>{row.section.name}</TableCell>
                  <TableCell>{row.reason || "-"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Profile">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/scholar-register/students/${row.studentId}`)
                        }
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {hasPermission("RE_STUDY_MANAGE") && (
                      <Tooltip title="Remove Record">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddReStudyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={fetchRecords}
      />
    </Container>
  );
};

export default ReStudyList;
